import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { isEmpty, isEqual } from 'lodash';
import get from 'lodash/get';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import type { TAccountProduct } from '../../common';
import { ProductSchema } from '../../common/types/Product';
import { getFeePaidByLabel } from '../../common/utils/fees';
import { createMutationPromise } from '../../common/utils/queries';
import {
  mutateMerchantDetails,
  mutateProductData,
  mutateProductFees,
  productFeeDefaults,
  useAccountStore,
  useProductStore
} from '../../contexts';
import { MerchantSchema } from '../../contexts/MerchantSchema';
import { BlockContainer, CollapsibleSection, Loading } from '../atoms';
import type { EditSchemaModalProps } from '../molecules';
import {
  AccountFeeMatrix,
  AccountProductsAddList,
  CustomFeeBlock,
  EditSchemaModal
} from '../molecules';
import { GenericFeeMatrix } from '../molecules/GenericFeeMatrix';
import { FEE_IDS, FEE_LABELS } from '../types';
import type { AccountProductBlock } from '../types/Product';
import { AccountServicingInitialFeeMatrix } from './AccountServicingInitialFeeMatrix';
import { BaseInitialFeeMatrix } from './BaseInitialFeeMatrix';

const EditProductModalSchema = ProductSchema.omit({
  fees: true,
  feeMatrix: true
});

const AccountProductsListItem = (product: TAccountProduct) => {
  const {
    productType = '',
    availableTerms = [],
    minBureauScore = 0,
    fees = {}
  } = product;
  const [merchantModalOpen, setMerchantModalOpen] = useState(false);
  const [editProductModalOpen, setEditProductModalOpen] = useState(false);
  const [editFeeModal, setEditFeeModal] = useState<FEE_IDS>();
  const availableTypes = useProductStore((s) => s.availableTypes);
  const productTypeDetails = availableTypes.find(
    ({ code }) => code === productType
  );
  const { merchantId = '' } = useParams();
  // zustand store
  const setMerchantDetails = useAccountStore((s) => s.setMerchantDetails);
  const merchantDetails = useAccountStore((s) => s.merchantDetails);
  const updateProductData = useAccountStore((s) => s.updateProductData);
  const updateProductMutation = mutateProductData({
    productId: product.id,
    merchantId,
    updateProductData,
    mutate: useMutation
  });
  const updateProductFees = useAccountStore((s) => s.updateProductFees);
  const updateProductFeesMutation = mutateProductFees({
    productId: product.id,
    merchantId,
    updateProductFees,
    mutate: useMutation
  });
  const updateMerchantDetailsMutation = mutateMerchantDetails({
    merchantId,
    setMerchantDetails,
    mutate: useMutation
  });

  const updateFeeTerms =
    (id: FEE_IDS) =>
    async (terms: Array<any>): Promise<TAccountProduct['fees']> => {
      // @TODO - apply terms to product fees and update model
      // @TODO, ensure that we're updating feeMatrix and not just terms
      const fee = fees[id];

      if (!fee) {
        console.error(`Product with productId ${id} not found`);
        return fees;
      }

      const updatedFees: TAccountProduct['fees'] = {
        ...fees,
        [id]: {
          ...fee,
          conditions: { terms }
        }
      };

      const mutation = await createMutationPromise(
        updateProductFeesMutation,
        updatedFees
      );

      return mutation.fees;
    };

  const getDefaultFeeBlockProps = (feeId: FEE_IDS) => {
    return {
      size: 'full' as const,
      id: feeId,
      schema: ProductSchema,
      title: `${FEE_LABELS[feeId]} Settings`,
      modal: {
        columns: getFeeColumns(feeId),
        parseData: ({ fees = {}, ...data }) => {
          return {
            ...data,
            fees: Object.fromEntries(
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              Object.entries(fees).map(([key, { id = key, ...fee }]) => [
                key,
                productFeeDefaults({
                  id,
                  ...fee
                })
              ])
            )
          };
        },
        onSubmit: async ({
          data: { id, ...data }
          // formState: { dirtyFields }
        }) => {
          // NOTE: dirtyFields sometimes fails to pick up on changes when you
          // click the submit button directly after editing an input. Don't use!!
          const changedData = Object.fromEntries(
            Object.entries(data || {}).filter(
              ([k, v]) => !isEqual(v, get(product, k))
            )
          );
          const productFeesChanged = 'fees' in changedData;

          if (productFeesChanged) {
            await createMutationPromise(
              updateProductFeesMutation,
              changedData.fees as TAccountProduct['fees']
            );
          }
        }
      }
    } satisfies Partial<AccountProductBlock>;
  };
  const getFeeColumns = (
    id: FEE_IDS
  ): EditSchemaModalProps<typeof ProductSchema>['columns'] => [
    [
      {
        title: 'Fee paid by',
        contentType: 'text',
        // nonEditable: true,
        hidden:
          id !== FEE_IDS.AccountServicePlannedFee &&
          (get(product, `fees.${id}.feePaidBy`) === undefined ||
            id !== FEE_IDS.EstablishmentUpfrontFee),
        name: `fees.${id}.feePaidBy`,
        editingOptions: {
          complexOptions: [
            {
              text: 'Student',
              value: 'student'
            },
            {
              text: 'EP',
              value: 'EP'
            }
          ],
          required: true,
          type: 'select'
        },
        content: get(product, `fees.${id}.feePaidBy`)
          ? getFeePaidByLabel(get(product, `fees.${id}.feePaidBy`, ''))
          : undefined
      },
      {
        title: 'Days in Arrears',
        contentType: 'number',
        content: get(product, `fees.${id}.daysInArrears`),
        hidden:
          get(product, `fees.${id}.daysInArrears`) === undefined ||
          id !== FEE_IDS.ArrearsFee,
        name: `fees.${id}.daysInArrears`
      }
      // {
      //   title: 'GST',
      //   contentType: 'percent*100',
      //   options: {
      //     maxDigits: 2
      //   },
      //   content: get(product, `fees.${id}.gst`),
      //   hidden: get(product, `fees.${id}.gst`) === undefined,
      //   name: `fees.${id}.gst`
      // },
      // {
      //   title: 'System Creation Method',
      //   contentType: 'text',
      //   content: get(product, `fees.${id}.systemCreationMethod`),
      //   hidden: get(product, `fees.${id}.systemCreationMethod`) === undefined,
      //   name: `fees.${id}.systemCreationMethod`
      // }
      // {
      //   title: 'Default Fee Amount',
      //   contentType: 'currency',
      //   content: get(product, `fees.${id}.amount`),
      //   hidden: get(product, `fees.${id}.amount`) === undefined,
      //   name: `fees.${id}.amount`
      // }
    ],
    [
      {
        title: 'Amortised',
        contentType: 'check',
        content: get(product, `fees.${id}.amortized`),
        name: `fees.${id}.amortized`,
        hidden:
          get(product, `fees.${id}.amortized`) === undefined ||
          id !== FEE_IDS.EstablishmentUpfrontFee
      }
    ]
  ];

  const productIsSnp = productType.startsWith('SNP');
  const merchantBlock = {
    id: 'merchant',
    size: 'half',
    title: 'Merchant Settings',
    schema: MerchantSchema,
    hidden: !productIsSnp,
    editBlock: {
      onEdit: () => setMerchantModalOpen(true),
      label: 'Edit Settings'
    },
    modal: {
      columns: [
        [
          {
            title: 'Has package',
            contentType: 'check',
            content: merchantDetails?.merchantOnPackage,
            name: 'merchantOnPackage'
          },
          {
            title: 'Package has same terms',
            contentType: 'check',
            content: merchantDetails?.sameTermsFlag,
            name: 'sameTermsFlag'
          },
          {
            title: 'Manual Convert to PAS',
            contentType: 'check',
            content: merchantDetails?.needsManualReviewBeforeDowngrade,
            name: 'needsManualReviewBeforeDowngrade'
          },
          {
            title: 'Min Score Required',
            contentType: 'check',
            content: merchantDetails?.merchantMinScoreRequired,
            name: 'merchantMinScoreRequired'
          },
          {
            title: 'Approve Pending ID',
            contentType: 'check',
            content: merchantDetails?.approvePendingIdFlag,
            name: 'approvePendingIdFlag'
          },
          {
            title: 'Completes PAS Application',
            contentType: 'check',
            content: merchantDetails?.completesPASApplication,
            name: 'completesPASApplication'
          }
        ]
      ],
      onSubmit: async ({
        data: { id, ...data }
        // formState: { dirtyFields }
      }) => {
        // NOTE: dirtyFields sometimes fails to pick up on changes when you
        // click the submit button directly after editing an input. Don't use!!
        const changedData = Object.fromEntries(
          Object.entries(data || {}).filter(
            ([k, v]) => !isEqual(v, get(merchantDetails, k))
          )
        );

        if (!isEmpty(changedData)) {
          await createMutationPromise(
            updateMerchantDetailsMutation,
            changedData
          );
        }
      }
    }
  } satisfies AccountProductBlock;

  const productBlock = {
    id: 'product',
    size: 'half',
    title: 'Product Details',
    schema: EditProductModalSchema,
    modal: {
      columns: [
        [
          {
            title: 'Loan Terms',
            contentType: 'array',
            itemContentType: 'number',
            content: availableTerms,
            name: 'availableTerms',
            defaultValue: [1]
          },
          {
            title: 'Min Bureau Score',
            contentType: 'number',
            name: 'minBureauScore' as const,
            content: minBureauScore
          },
          {
            title: 'Min Loan Amount',
            contentType: 'currency',
            content: product.minLoanAmount,
            name: 'minLoanAmount' as const
          },
          {
            title: 'Max Loan Amount',
            contentType: 'currency',
            content: product.maxLoanAmount,
            name: 'maxLoanAmount' as const
          }
        ]
      ],
      onSubmit: async ({
        data: { id, ...data }
        // formState: { dirtyFields }
      }) => {
        // NOTE: dirtyFields sometimes fails to pick up on changes when you
        // click the submit button directly after editing an input. Don't use!!
        const changedData = Object.fromEntries(
          Object.entries(data || {}).filter(
            ([k, v]) => !isEqual(v, get(product, k))
          )
        );
        const productDataChanged = !isEmpty(changedData);

        if (productDataChanged) {
          await createMutationPromise(
            updateProductMutation,
            changedData as unknown as TAccountProduct
          );
        }
      }
    },
    editBlock: {
      onEdit: () => setEditProductModalOpen(true),
      label: 'Edit Settings'
    }
  } satisfies AccountProductBlock;
  const customFeeBlock = (
    feeId,
    usePercent = false,
    {
      InitialFeeMatrix = BaseInitialFeeMatrix,
      FeeMatrix = GenericFeeMatrix
    } = {}
  ) => ({
    ...getDefaultFeeBlockProps(feeId),
    editBlock: {
      onEdit: () => setEditFeeModal(feeId),
      label: 'Edit Fee'
    },
    matrix: {
      FooterInitial: () => (
        <InitialFeeMatrix
          merchantId={merchantId}
          feeId={feeId}
          onUpdate={updateFeeTerms(feeId)}
          product={product}
          usePercent={usePercent}
        />
      ),
      FooterBlock: () => (
        <FeeMatrix
          feeId={feeId}
          onUpdate={updateFeeTerms(feeId)}
          product={product}
          merchantId={merchantId}
          usePercent={usePercent}
        />
      )
    }
  });
  const customFeeBlocks = [
    productBlock,
    merchantBlock,
    customFeeBlock(FEE_IDS.EstablishmentUpfrontFee),
    ...(productType === 'PAD' ? [customFeeBlock(FEE_IDS.DepositFee)] : []),
    customFeeBlock(FEE_IDS.AccountServicePlannedFee, false, {
      FeeMatrix: AccountFeeMatrix,
      InitialFeeMatrix: AccountServicingInitialFeeMatrix
    }),
    customFeeBlock(FEE_IDS.DefaultFee),
    customFeeBlock(FEE_IDS.ArrearsFee),
    customFeeBlock(FEE_IDS.MerchantServiceFee, true)
  ] satisfies ReadonlyArray<AccountProductBlock>;

  const editFeeModalBlock = customFeeBlocks.find(
    (b) => b.id === editFeeModal
  ) as AccountProductBlock | undefined;

  return (
    <CollapsibleSection
      title={productTypeDetails?.productCatalogueId || productType}
      key={productType}
    >
      <EditSchemaModal<typeof EditProductModalSchema>
        isModalOpen={editProductModalOpen}
        handleModalClose={() => setEditProductModalOpen(false)}
        itemSubtitle={product.productType}
        itemTitle={'Product'}
        schema={productBlock.schema}
        data={product}
        {...productBlock.modal}
      />

      {merchantDetails && (
        <EditSchemaModal<typeof MerchantSchema>
          isModalOpen={merchantModalOpen}
          handleModalClose={() => setMerchantModalOpen(false)}
          data={merchantDetails}
          schema={merchantBlock.schema}
          itemTitle={'Merchant'}
          itemSubtitle={'Merchant Details'}
          {...merchantBlock.modal}
        />
      )}

      {editFeeModalBlock && (
        <EditSchemaModal<typeof EditProductModalSchema>
          isModalOpen={true}
          schema={editFeeModalBlock.schema as any}
          itemTitle={'Fee'}
          handleModalClose={() => setEditFeeModal(undefined)}
          data={product}
          itemSubtitle={editFeeModalBlock.title}
          {...editFeeModalBlock.modal}
        />
      )}

      <BlockContainer>
        {customFeeBlocks.map(({ modal: { columns }, ...rest }) => (
          <CustomFeeBlock
            key={uuidv4()}
            merchantId={merchantId}
            product={product}
            columns={columns}
            {...rest}
          />
        ))}
      </BlockContainer>
    </CollapsibleSection>
  );
};

const AccountProducts = () => {
  const currentProducts = useAccountStore((s) => s.currentProducts);
  const isLoading = useAccountStore((s) => s.currentProductsLoading);
  const { merchantId = '' } = useParams();

  return (
    <>
      <AccountProductsAddList merchantId={merchantId} />
      {currentProducts?.map((product) => (
        <AccountProductsListItem {...product} key={product.productType} />
      ))}
      {isLoading && <Loading key={uuidv4()} />}
    </>
  );
};

export { AccountProducts };
